import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { Textarea } from '../components/Textarea';

export const QueryDetailPage = () => {
  // Mock query data - in real app, would fetch based on ID
  const queryId = 2;

  const [notes, setNotes] = useState('Agent requested partial after reading query. Need to send first 50 pages + updated synopsis within 2 weeks.');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  const query = {
    id: 2,
    manuscriptId: 1,
    agentName: 'Jennifer Wu',
    agencyName: 'Wu & Partners Literary',
    agentEmail: 'submissions@wupartnersliterary.com',
    manuscriptTitle: 'Echoes of the Archive',
    manuscriptGenre: 'Historical Fiction',
    manuscriptWordCount: '95,000',
    submittedDate: '2024-01-10',
    status: 'partial-request',
    queryLetter: 'Dear Ms. Wu,\n\nI am seeking representation for my historical fiction manuscript, ECHOES OF THE ARCHIVE, complete at 95,000 words. This book will appeal to readers of THE ROSE CODE by Kate Quinn and THE ENIGMA GAME by Elizabeth Wein.\n\nIn 1942, Margaret Chen is a young archivist at the National Archives who discovers a cache of coded letters that reveal a conspiracy reaching the highest levels of government. As she races to decode the messages before they fall into enemy hands, she must decide between her duty to preserve history and her moral obligation to prevent a catastrophe.\n\nBorn in San Francisco to Chinese immigrant parents, Margaret has spent her life proving she belongs. When she uncovers the letters hidden in a routine acquisition, she recognizes immediately that they\'re written in a variation of the Chinese telegraph code her father taught her as a child. The messages speak of weapons shipments, secret meetings, and a plot that could change the course of the war.\n\nBut Margaret\'s discovery puts her in danger. Government officials want the letters buried, not decoded. Her colleagues question her loyalty. And the deeper she digs, the more she realizes that some secrets were meant to stay hidden. With time running out and enemies closing in, Margaret must choose between staying silent or risking everything to expose the truth.\n\nI have an MFA in Creative Writing from Columbia University and my short fiction has appeared in The Southern Review and Ploughshares. I currently work as a research librarian, which inspired this novel\'s archival setting.\n\nThank you for your time and consideration. I look forward to hearing from you.\n\nSincerely,\nYour Name',
    synopsis: 'In 1942, Margaret Chen, a Chinese-American archivist at the National Archives in Washington D.C., discovers a cache of coded letters during a routine processing of new acquisitions...\n\n[Full synopsis continues with plot details, character arcs, and resolution]',
    firstPages: 'Chapter One\n\nThe smell of old paper and leather bindings had become as familiar to Margaret as her own heartbeat...\n\n[First 10 pages of the manuscript]',
    timeline: [
      { date: '2024-01-10', event: 'Query submitted', type: 'submitted' },
      { date: '2024-01-11', event: 'Query received by agent', type: 'received' },
      { date: '2024-01-20', event: 'Agent requested partial manuscript (first 50 pages)', type: 'response' },
    ],
    agentResponse: {
      date: '2024-01-20',
      type: 'partial',
      message: 'Dear Author,\n\nThank you for querying me with ECHOES OF THE ARCHIVE. Your premise is intriguing, and I love the combination of WWII history with archival mysteries. Margaret sounds like a compelling protagonist, and the use of the Chinese telegraph code is a unique hook.\n\nI would love to see more. Could you please send me the first 50 pages of your manuscript along with a full synopsis?\n\nI look forward to reading!\n\nBest regards,\nJennifer Wu',
      requestDetails: {
        materialsRequested: 'First 50 pages + full synopsis',
        deadline: '2 weeks from response date',
        format: 'Email attachment (Word or PDF)',
      },
    },
  };

  const handleSaveNotes = () => {
    setIsSavingNotes(true);
    console.log('Saving notes:', notes);
    setTimeout(() => {
      setIsSavingNotes(false);
      window.alert('Notes saved successfully!');
    }, 500);
  };

  const handleSubmitMaterials = () => {
    window.location.href = `/writer/queries/${queryId}/submit-materials`;
  };

  const handleWithdraw = () => {
    if (window.confirm('Are you sure you want to withdraw this query? This action cannot be undone.')) {
      console.log('Withdrawing query:', queryId);
      window.alert('Query withdrawn.');
      window.location.href = '/writer/queries';
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: any; label: string }> = {
      pending: { variant: 'warning', label: 'Pending' },
      'partial-request': { variant: 'seeking', label: 'Partial Requested' },
      'full-request': { variant: 'success', label: 'Full Requested' },
      rejected: { variant: 'default', label: 'Rejected' },
    };
    const config = statusMap[status] || statusMap.pending;
    return <Badge variant={config.variant} size="large">{config.label}</Badge>;
  };

  const getTimelineIcon = (type: string) => {
    const icons: Record<string, string> = {
      submitted: '📤',
      received: '✓',
      response: '📧',
    };
    return icons[type] || '•';
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => window.location.href = '/writer/queries'}
          style={{ marginBottom: '16px' }}
        >
          ← Back to My Queries
        </Button>

        {/* Header */}
        <Card style={{ marginBottom: '32px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px',
            gap: '16px',
          }}>
            <div style={{ flex: 1 }}>
              <h1 style={{
                fontSize: '32px',
                fontWeight: '400',
                marginBottom: '8px',
                color: theme.colors.textPrimary,
              }}>
                Query to {query.agentName}
              </h1>
              <div style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
                marginBottom: '12px',
              }}>
                {query.agencyName}
              </div>
            </div>
            {getStatusBadge(query.status)}
          </div>

          {/* Quick Info */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            padding: '16px',
            backgroundColor: theme.colors.backgroundAlt,
            borderRadius: theme.borderRadius.medium,
          }}>
            <div>
              <div style={{
                fontSize: '13px',
                color: theme.colors.textSecondary,
                marginBottom: '4px',
              }}>
                Manuscript
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
              }}>
                {query.manuscriptTitle}
              </div>
              <div style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                fontFamily: theme.fonts.monospace,
              }}>
                {query.manuscriptGenre} • {query.manuscriptWordCount} words
              </div>
            </div>

            <div>
              <div style={{
                fontSize: '13px',
                color: theme.colors.textSecondary,
                marginBottom: '4px',
              }}>
                Submitted
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
                fontFamily: theme.fonts.monospace,
              }}>
                {new Date(query.submittedDate).toLocaleDateString()}
              </div>
            </div>

            <div>
              <div style={{
                fontSize: '13px',
                color: theme.colors.textSecondary,
                marginBottom: '4px',
              }}>
                Contact
              </div>
              <a
                href={`mailto:${query.agentEmail}`}
                style={{
                  fontSize: '14px',
                  color: theme.colors.primary,
                  textDecoration: 'none',
                  fontFamily: theme.fonts.monospace,
                }}
              >
                {query.agentEmail}
              </a>
            </div>
          </div>
        </Card>

        {/* Main Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '32px',
        }}>
          {/* Left Column */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}>
            {/* Status Timeline */}
            <Card>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '500',
                marginBottom: '20px',
                color: theme.colors.textPrimary,
              }}>
                Status Timeline
              </h2>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}>
                {query.timeline.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      gap: '16px',
                      paddingBottom: index < query.timeline.length - 1 ? '16px' : '0',
                      borderBottom: index < query.timeline.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                    }}
                  >
                    <div style={{
                      fontSize: '24px',
                      width: '32px',
                      flexShrink: 0,
                    }}>
                      {getTimelineIcon(item.type)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: theme.colors.textPrimary,
                        marginBottom: '4px',
                      }}>
                        {item.event}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: theme.colors.textTertiary,
                        fontFamily: theme.fonts.monospace,
                      }}>
                        {new Date(item.date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Agent Response */}
            {query.agentResponse && (
              <Card>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '500',
                  marginBottom: '16px',
                  color: theme.colors.textPrimary,
                }}>
                  Agent Response
                </h2>

                <div style={{
                  padding: '16px',
                  backgroundColor: theme.colors.successLight,
                  borderRadius: theme.borderRadius.medium,
                  marginBottom: '20px',
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: theme.colors.success,
                    fontWeight: '500',
                    marginBottom: '4px',
                  }}>
                    {query.agentResponse.type === 'partial' && '📧 Partial Manuscript Requested'}
                    {query.agentResponse.type === 'full' && '📚 Full Manuscript Requested'}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: theme.colors.textTertiary,
                    fontFamily: theme.fonts.monospace,
                  }}>
                    Received: {new Date(query.agentResponse.date).toLocaleDateString()}
                  </div>
                </div>

                <div style={{
                  padding: '20px',
                  backgroundColor: theme.colors.backgroundAlt,
                  borderRadius: theme.borderRadius.medium,
                  marginBottom: '20px',
                }}>
                  <p style={{
                    fontSize: '15px',
                    lineHeight: '1.8',
                    color: theme.colors.textPrimary,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {query.agentResponse.message}
                  </p>
                </div>

                {/* Request Details */}
                <div style={{
                  padding: '16px',
                  backgroundColor: theme.colors.primaryLight,
                  borderRadius: theme.borderRadius.medium,
                  marginBottom: '20px',
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    marginBottom: '12px',
                    color: theme.colors.textPrimary,
                  }}>
                    Materials Requested
                  </h3>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    fontSize: '15px',
                    color: theme.colors.textPrimary,
                  }}>
                    <div>
                      <span style={{ color: theme.colors.textSecondary }}>Materials: </span>
                      <strong>{query.agentResponse.requestDetails.materialsRequested}</strong>
                    </div>
                    <div>
                      <span style={{ color: theme.colors.textSecondary }}>Deadline: </span>
                      <strong>{query.agentResponse.requestDetails.deadline}</strong>
                    </div>
                    <div>
                      <span style={{ color: theme.colors.textSecondary }}>Format: </span>
                      <strong>{query.agentResponse.requestDetails.format}</strong>
                    </div>
                  </div>
                </div>

                <Button fullWidth onClick={handleSubmitMaterials}>
                  Submit Requested Materials
                </Button>
              </Card>
            )}

            {/* Query Materials */}
            <Card>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Submitted Materials
              </h2>

              {/* Query Letter */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: theme.colors.textPrimary,
                }}>
                  Query Letter
                </h3>
                <div style={{
                  padding: '16px',
                  backgroundColor: theme.colors.backgroundAlt,
                  borderRadius: theme.borderRadius.medium,
                  fontSize: '14px',
                  lineHeight: '1.8',
                  color: theme.colors.textPrimary,
                  whiteSpace: 'pre-wrap',
                  maxHeight: '400px',
                  overflow: 'auto',
                }}>
                  {query.queryLetter}
                </div>
              </div>

              {/* Synopsis */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: theme.colors.textPrimary,
                }}>
                  Synopsis
                </h3>
                <div style={{
                  padding: '16px',
                  backgroundColor: theme.colors.backgroundAlt,
                  borderRadius: theme.borderRadius.medium,
                  fontSize: '14px',
                  lineHeight: '1.8',
                  color: theme.colors.textPrimary,
                  whiteSpace: 'pre-wrap',
                  maxHeight: '300px',
                  overflow: 'auto',
                }}>
                  {query.synopsis}
                </div>
              </div>

              {/* First Pages */}
              <div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: theme.colors.textPrimary,
                }}>
                  First Pages
                </h3>
                <div style={{
                  padding: '16px',
                  backgroundColor: theme.colors.backgroundAlt,
                  borderRadius: theme.borderRadius.medium,
                  fontSize: '13px',
                  lineHeight: '1.8',
                  color: theme.colors.textPrimary,
                  whiteSpace: 'pre-wrap',
                  fontFamily: theme.fonts.monospace,
                  maxHeight: '300px',
                  overflow: 'auto',
                }}>
                  {query.firstPages}
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}>
            {/* Quick Actions */}
            <Card>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Actions
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => window.location.href = `/agents/${query.id}`}
                >
                  View Agent Profile
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => window.location.href = `/writer/manuscripts/${query.manuscriptId}`}
                >
                  View Manuscript
                </Button>
                {query.status === 'pending' && (
                  <Button
                    variant="ghost"
                    fullWidth
                    onClick={handleWithdraw}
                    style={{ color: theme.colors.error }}
                  >
                    Withdraw Query
                  </Button>
                )}
              </div>
            </Card>

            {/* Notes */}
            <Card>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Private Notes
              </h3>
              <Textarea
                placeholder="Add personal notes about this query (only visible to you)..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={8}
              />
              <Button
                variant="outline"
                fullWidth
                onClick={handleSaveNotes}
                disabled={isSavingNotes}
                style={{ marginTop: '12px' }}
              >
                {isSavingNotes ? 'Saving...' : 'Save Notes'}
              </Button>
            </Card>

            {/* Info Card */}
            <Card style={{ backgroundColor: theme.colors.backgroundAlt }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '500',
                marginBottom: '12px',
                color: theme.colors.textPrimary,
              }}>
                Query Tips
              </h3>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                fontSize: '14px',
                lineHeight: '1.6',
                color: theme.colors.textSecondary,
              }}>
                <li>Don't follow up before the agent's stated response time</li>
                <li>If you receive a request, respond promptly and professionally</li>
                <li>Keep track of all correspondence in your notes</li>
                <li>A "no" from one agent doesn't reflect on your work's quality</li>
              </ul>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
